import type { SVGProps } from "react";

/**
 * Shared accessibility contract for the brand marks (AWS, Azure, Google Cloud,
 * Google, Microsoft).
 *
 * Brand marks are **decorative by default**. Every current call site pairs a
 * mark with its own visible text label — `<GoogleIcon /> Google` inside a
 * button, `<AzureIcon /> Azure` in the provider row — so a `role="img"` +
 * `aria-label` on the SVG duplicates that label and the control announces
 * "Google Google" / "Microsoft Azure Azure".
 *
 * Passing an explicit `aria-label` opts back in to a labelled standalone mark:
 * that is the signal that the icon is carrying the meaning on its own, with no
 * adjacent text to name it.
 *
 * Spread this BEFORE `{...props}` so an explicit consumer prop still wins:
 *
 *     <svg {...brandIconA11y(props)} className={className} {...props}>
 */
export function brandIconA11y(props: SVGProps<SVGSVGElement>) {
  const labelled = props["aria-label"] != null;
  return {
    // Brand marks are never focus targets; the control around them is.
    focusable: "false",
    "aria-hidden": labelled ? undefined : true,
    role: labelled ? "img" : undefined,
  } as const;
}
